import React, { useMemo, useState } from "react";
import AdminEventSetup from "./AdminEventSetup";
import DailyReport from "./DailyReport";
import MyHistory from "./MyHistory";
import PressFeeTracker from "./PressFeeTracker";
import RoleManager from "./RoleManager";
import PaymentStatus from "./PaymentStatus";
import TopEarners from "./TopEarners";
import NotificationBar from "./NotificationBar";
import AdminTeamPreview from "./AdminTeamPreview";
import AuditLog from "./AuditLog";
import { auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

const MENU_GROUPS = [
  {
    label: "งาน",
    items: [
      { key: "setup", icon: "▦", label: "จัดการงาน", sub: "เพิ่ม แก้ไข และจัดเก็บงาน" },
      { key: "press", icon: "✎", label: "กรอกค่ากด", sub: "กรอกแทนทีมเมื่อจำเป็น" },
      { key: "history", icon: "◷", label: "ประวัติ", sub: "ตรวจรายการย้อนหลัง" },
    ],
  },
  {
    label: "การเงิน",
    items: [
      { key: "report", icon: "◫", label: "การเงิน & รายงาน", sub: "กำไร งาน และรายละเอียด" },
      { key: "payment", icon: "฿", label: "โอนค่ากด", sub: "ยอดค้างและสถานะการโอน" },
      { key: "top", icon: "☆", label: "ผลงานทีม", sub: "ดูผลงานแยกตามทีม" },
    ],
  },
  {
    label: "ระบบ",
    items: [
      { key: "role", icon: "◎", label: "จัดการผู้ใช้", sub: "Role · ชื่อทีม · ชื่อจริง" },
      { key: "audit", icon: "≡", label: "Audit Log", sub: "ประวัติการแก้ไขรายการเงินจริง" },
    ],
  },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("setup");
  const [message, setMessage] = useState("");
  const [showTeamPreview, setShowTeamPreview] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const currentUser = auth.currentUser;

  const currentMenu = useMemo(
    () => MENU_GROUPS.flatMap((group) => group.items).find((item) => item.key === tab),
    [tab]
  );

  const adminInitial = (
    currentUser?.displayName || currentUser?.email || "A"
  ).trim().charAt(0).toUpperCase();

  const showNotification = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 4000);
  };

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setMobileMenuOpen(false);
  };

  if (showTeamPreview) {
    return <AdminTeamPreview onExit={() => setShowTeamPreview(false)} />;
  }

  return (
    <div className="admin-shell">
      <NotificationBar message={message} onClose={() => setMessage("")} />

      {mobileMenuOpen && (
        <button
          type="button"
          className="admin-shell__scrim"
          aria-label="ปิดเมนู"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${mobileMenuOpen ? "is-open" : ""}`}>
        <div className="admin-brand">
          <div className="admin-brand__mark">YY</div>
          <div className="admin-brand__copy">
            <strong>ยยมือทอง</strong>
            <span>Operations</span>
          </div>
          <button
            type="button"
            className="admin-sidebar__close"
            aria-label="ปิดเมนู"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>

        <button
          type="button"
          className="admin-preview-card"
          onClick={() => {
            setShowTeamPreview(true);
            setMobileMenuOpen(false);
          }}
        >
          <span className="admin-preview-card__icon">◉</span>
          <span className="admin-preview-card__copy">
            <strong>ดูในมุมทีม</strong>
            <small>Preview โดยไม่เปลี่ยน Role</small>
          </span>
          <span className="admin-preview-card__arrow">›</span>
        </button>

        <nav className="admin-nav" aria-label="เมนูแอดมิน">
          {MENU_GROUPS.map((group) => (
            <div className="admin-nav__group" key={group.label}>
              <span className="admin-nav__group-label">{group.label}</span>
              <div className="admin-nav__group-items">
                {group.items.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    className={`admin-nav__item ${tab === item.key ? "is-active" : ""}`}
                    onClick={() => switchTab(item.key)}
                    aria-current={tab === item.key ? "page" : undefined}
                  >
                    <span className="admin-nav__icon">{item.icon}</span>
                    <span className="admin-nav__copy">
                      <strong>{item.label}</strong>
                      <small>{item.sub}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__account">
          <div className="admin-account-card">
            <div className="admin-account-card__avatar">{adminInitial}</div>
            <div className="admin-account-card__copy">
              <strong>{currentUser?.displayName || "Administrator"}</strong>
              <span>{currentUser?.email || "Admin account"}</span>
            </div>
          </div>
          <button
            type="button"
            className="admin-logout-button"
            onClick={() => navigate("/logout")}
          >
            <span>↪</span>
            ออกจากระบบ
          </button>
        </div>
      </aside>

      <section className="admin-workspace">
        <header className="admin-mobile-header">
          <button
            type="button"
            className="admin-mobile-menu-button"
            aria-label="เปิดเมนู"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
          <div className="admin-mobile-header__copy">
            <small>ADMIN</small>
            <strong>{currentMenu?.label || "แอดมิน"}</strong>
          </div>
          <div className="admin-mobile-avatar">{adminInitial}</div>
        </header>

        <div className="admin-context-bar">
          <div>
            <span>ADMIN / {currentMenu?.label || "ระบบ"}</span>
            <strong>{currentMenu?.label || "แอดมิน"}</strong>
          </div>
          <div className="admin-context-bar__status">
            <span className="admin-context-bar__dot" />
            Firebase connected
          </div>
        </div>

        <main className="admin-main">
          {tab === "setup" && <AdminEventSetup showNotification={showNotification} />}
          {tab === "report" && <DailyReport />}
          {tab === "role" && <RoleManager />}
          {tab === "press" && <PressFeeTracker />}
          {tab === "history" && <MyHistory />}
          {tab === "payment" && <PaymentStatus showNotification={showNotification} />}
          {tab === "top" && <TopEarners />}
          {tab === "audit" && <AuditLog />}
        </main>
      </section>
    </div>
  );
}
